import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ChevronRight, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const FAQ_ITEMS = [
  { title: "Cancelamento ou reembolso de ingressos", link: "/faq" },
  { title: "Compra pendente", link: "/faq" },
  { title: "Troca de titularidade", link: "/faq" },
  { title: "Como acesso meu certificado", link: "/faq" },
  { title: "Não consigo acessar minha conta", link: "/faq" },
  { title: "Sobre emissão de nota fiscal", link: "/faq" },
];

interface ContactProducerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  producerId: string;
  producerName: string;
  eventId?: string;
  eventTitle?: string;
  initialSubject?: string;
  initialDirectToForm?: boolean;
}

export function ContactProducerModal({
  open,
  onOpenChange,
  producerId,
  producerName,
  eventId,
  eventTitle,
  initialSubject,
  initialDirectToForm = false,
}: ContactProducerModalProps) {
  const { t } = useTranslation("eventDetail");
  const { user, profile } = useAuth();
  const [showForm, setShowForm] = useState(initialDirectToForm);

  // Set form visibility and prefill when modal opens
  useEffect(() => {
    if (open) {
      setShowForm(Boolean(initialDirectToForm));
      setFormData((prev) => ({
        ...prev,
        name: prev.name || profile?.full_name || "",
        email: prev.email || user?.email || "",
        subject: initialSubject || prev.subject || "",
      }));
    }
  }, [open, initialDirectToForm, initialSubject, profile?.full_name, user?.email]);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: initialSubject || "",
    message: "",
  });

  // Auto-fill from auth
  const handleShowForm = () => {
    if (!user) {
      toast({
        title: t("contactModal.toast.loginTitle"),
        description: t("contactModal.toast.loginDesc"),
        variant: "destructive",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      name: prev.name || profile?.full_name || "",
      email: prev.email || user?.email || "",
    }));
    setShowForm(true);
  };

  const handleSend = async () => {
    if (!user) {
      toast({ title: t("contactModal.toast.loginRequiredTitle"), description: t("contactModal.toast.loginRequiredDesc"), variant: "destructive" });
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      toast({ title: t("contactModal.toast.fillFields"), variant: "destructive" });
      return;
    }

    setSending(true);
    try {
      const payload = {
        producer_id: producerId,
        sender_id: user.id,
        sender_name: formData.name,
        sender_email: formData.email,
        event_id: eventId || null,
        subject: formData.subject,
        message: formData.message,
      };

      let { data: insertedMsg, error } = await supabase.from("producer_messages").insert(payload).select().single();
      if (error && /event_id/i.test(error.message || "")) {
        const { event_id: _ignored, ...legacyPayload } = payload;
        const legacyInsert = await supabase.from("producer_messages").insert(legacyPayload).select().single();
        error = legacyInsert.error;
        insertedMsg = legacyInsert.data;
      }

      if (error) throw error;
      
      // Trigger email notification for the producer
      if (insertedMsg) {
        await supabase.functions.invoke("send-message-notification", {
          body: { messageId: insertedMsg.id, type: "new_message" }
        }).catch(console.error);
      }
      toast({ title: t("contactModal.toast.sentTitle"), description: t("contactModal.toast.sentDesc", { name: producerName }) });
      setFormData({ name: "", email: "", subject: "", message: "" });
      setShowForm(false);
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: t("contactModal.toast.errorTitle"), description: err.message || t("contactModal.toast.errorDesc"), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      setShowForm(false);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md p-6 max-h-[90vh] overflow-y-auto">
        {!showForm ? (
          <>
            <DialogHeader className="mb-4">
              <DialogTitle className="text-xl font-display font-bold">{t("contactModal.faqTitle")}</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t("contactModal.faqText")}{" "}
                <Link to="/faq" className="text-primary hover:underline font-medium" onClick={() => onOpenChange(false)}>
                  {t("contactModal.helpCenter")}
                </Link>
              </p>
            </DialogHeader>

            <div className="flex flex-col gap-2 mb-6">
              {FAQ_ITEMS.map((item) => (
                <Link
                  key={item.title}
                  to={item.link}
                  onClick={() => onOpenChange(false)}
                  className="flex items-center justify-between py-3 px-4 rounded-lg bg-muted/40 text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors group border border-transparent hover:border-primary/20"
                >
                  <span className="truncate pr-4">{item.title}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              ))}
            </div>

            <div className="pt-2 text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("contactModal.promptText")}
              </p>
              <Button className="w-full" onClick={handleShowForm}>
                {user ? t("contactModal.talkButton") : t("contactModal.loginTalkButton")}
              </Button>
              {!user && (
                <p className="text-xs text-muted-foreground">
                  {t("contactModal.loginRequiredNote")}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-display">{t("contactModal.formTitle", { name: producerName })}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {t("contactModal.formSubtitle", { eventInfo: eventTitle ? ` sobre ${eventTitle}` : "" })}
              </p>
            </DialogHeader>

            <div className="space-y-4">
              {!user && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 flex items-center justify-between gap-2">
                  <span>Conecte-se para enviar sua mensagem ao organizador.</span>
                  <Button size="sm" variant="outline" asChild className="h-7 text-xs bg-white">
                    <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
                      Entrar
                    </Link>
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("contactModal.nameLabel")}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    placeholder={t("contactModal.namePlaceholder")}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("contactModal.emailLabel")}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="email@exemplo.com"
                    disabled={!!user}
                    className={user ? "bg-muted" : ""}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t("contactModal.subjectLabel")}</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))}
                  placeholder={t("contactModal.subjectPlaceholder")}
                />
              </div>
              <div>
                <Label className="text-xs">{t("contactModal.messageLabel")}</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                  placeholder={t("contactModal.messagePlaceholder")}
                  rows={4}
                  maxLength={2000}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>
                  {t("contactModal.backButton")}
                </Button>
                <Button className="flex-1 gap-2" onClick={handleSend} disabled={sending}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {t("contactModal.sendButton")}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
